
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';
import {
  formatUsDateFromIso,
  formatUsDateFromDateTime,
  todayIsoLocal,
  parseUsDateToIso,
  normalizeUsDateTyping,
  resolveIsoFromUsForm,
} from '../../utils/usDateFormat';
import Loader from '../../components/Loader/Loader';
import ReusablePopup from '../../components/Modals/ReuseableModal';
import {
  fetchAnnouncementById,
  parseNotificationActionError,
} from '../../utils/notificationNavigation';

const canManageAnnouncements = (role) => role === 'admin' || role === 'super';

const AnnouncementDetailsPopup = ({ announcement, userProfile, onClose, onEdit, onDelete }) => {
  if (!announcement) return null;

  const attachments = Array.isArray(announcement.attachments) ? announcement.attachments : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-lg w-full max-h-[min(90vh,calc(100dvh-1.5rem))] overflow-y-auto overscroll-contain my-auto">
        <div className="flex justify-between items-start gap-3 mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 break-words pr-2 flex-1 min-w-0">
            {announcement.title}
          </h2>
        </div>
        <p className="text-gray-600 mb-4 whitespace-pre-wrap break-words text-sm sm:text-base">
          {announcement.description}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
          {canManageAnnouncements(userProfile.role) ? (
            <>
              <div>
                <span className="font-semibold">Created:</span>{' '}
                {formatAnnouncementCreatedDate(announcement.created_at)}
              </div>
              <div>
                <span className="font-semibold">Expires:</span>{' '}
                {formatAnnouncementDate(announcement.expiry_date)}
              </div>
              <div>
                <span className="font-semibold">Departments:</span>{' '}
                {Array.isArray(announcement.departments) && announcement.departments.length > 0
                  ? announcement.departments.join(', ')
                  : 'All departments'}
              </div>
              <div>
                <span className="font-semibold">Positions:</span>{' '}
                {Array.isArray(announcement.positions) && announcement.positions.length > 0
                  ? announcement.positions.join(', ')
                  : 'All positions'}
              </div>
              <div>
                <span className="font-semibold">Updated:</span>{' '}
                {announcement.updated_at ? formatUsDateFromDateTime(announcement.updated_at) : 'Never'}
              </div>
              <div>
                <span className="font-semibold">Created By:</span> {announcement.created_by || 'System'}
              </div>
              <div>
                <span className="font-semibold">Updated By:</span>{' '}
                {announcement.updated_at ? (announcement.updated_by || 'System') : 'Never'}
              </div>
            </>
          ) : (
            <>
              <div>
                <span className="font-semibold">Announcement Data:</span>{' '}
                {formatAnnouncementCreatedDate(announcement.created_at)}
              </div>
              {Array.isArray(announcement.departments) && announcement.departments.length > 0 && (
                <div>
                  <span className="font-semibold">Departments:</span> {announcement.departments.join(', ')}
                </div>
              )}
              {Array.isArray(announcement.positions) && announcement.positions.length > 0 && (
                <div>
                  <span className="font-semibold">Positions:</span> {announcement.positions.join(', ')}
                </div>
              )}
              <div>
                <span className="font-semibold">Updated:</span>{' '}
                {announcement.updated_at ? formatUsDateFromDateTime(announcement.updated_at) : 'Never'}
              </div>
              <div>
                <span className="font-semibold">Created By:</span> {announcement.created_by || 'System'}
              </div>
              <div>
                <span className="font-semibold">Updated By:</span>{' '}
                {announcement.updated_at ? (announcement.updated_by || 'System') : 'Never'}
              </div>
            </>
          )}
        </div>

        {attachments.length > 0 && (
          <div className="mt-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Attachments ({attachments.length})
            </h3>
            <ul className="space-y-2">
              {attachments.map((att) => (
                <li
                  key={att.id || att.file_url}
                  className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <div className="min-w-0 sm:mr-3 flex items-start sm:items-center gap-2">
                    <svg
                      className="w-5 h-5 text-[#674936] flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.414a4 4 0 10-5.656-5.656L4.343 11.172a6 6 0 108.485 8.485L20 12.5"
                      />
                    </svg>
                    <div className="min-w-0">
                      <p className="text-gray-800 font-medium truncate" title={att.file_name}>
                        {att.file_name}
                      </p>
                      <p className="text-xs text-gray-500">{formatFileSize(att.size)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 border-t border-gray-200 pt-2 sm:border-0 sm:pt-0">
                    <a
                      href={att.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#674936] hover:underline text-sm font-semibold touch-manipulation"
                    >
                      View
                    </a>
                    <a
                      href={att.file_url}
                      download={att.file_name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#674936] hover:underline text-sm font-semibold touch-manipulation"
                    >
                      Download
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between sm:items-center mt-6 pt-2 border-t border-gray-100">
        <div className="flex flex-wrap items-center gap-2 sm:space-x-3 sm:gap-0">
            {canManageAnnouncements(userProfile.role) && (<>
              <button
                onClick={() => onEdit(announcement)}
                className="p-2 text-white bg-[#674936] hover:bg-[#503220] rounded-lg transition-colors duration-200 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Edit announcement"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={() => onDelete(announcement.id)}
                className="p-2 text-white bg-[#674936] hover:bg-[#503220] rounded-lg transition-colors duration-200 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Delete announcement"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </>)}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full sm:w-auto bg-[#674936] text-white px-4 py-2.5 rounded-lg hover:bg-[#8B5F3D] transition-colors duration-300 font-semibold touch-manipulation shrink-0"
        >
          Close
        </button>
        </div>
      </div>
    </div>
  );
};

const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_MIME = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
]);

/** Extension → MIME when the browser omits type or sends application/octet-stream (same idea as profile/GCS uploads). */
const ATTACHMENT_EXT_TO_MIME = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
};

const effectiveAttachmentMime = (file) => {
  let t = (file.type || '').trim().toLowerCase();
  if (t === 'image/jpg' || t === 'image/pjpeg') t = 'image/jpeg';
  if (t && t !== 'application/octet-stream') return t;
  const name = (file.name || '').toLowerCase();
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot) : '';
  const fromExt = ATTACHMENT_EXT_TO_MIME[ext];
  if (fromExt) return fromExt;
  return t;
};

const ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.gif';

const formatFileSize = (bytes) => {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const validateAttachmentFiles = (files) => {
  for (const file of files) {
    const mime = effectiveAttachmentMime(file);
    if (!ALLOWED_ATTACHMENT_MIME.has(mime)) {
      return `"${file.name}" is not a supported file type.`;
    }
    if (file.size > MAX_FILE_BYTES) {
      return `"${file.name}" exceeds the 10 MB size limit.`;
    }
  }
  return null;
};

const isAnnouncementActive = (announcement) => {
  if (!announcement.expiry_date) return true;
  const part = String(announcement.expiry_date).split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(part)) {
    return part >= todayIsoLocal();
  }
  const expiryDate = new Date(announcement.expiry_date);
  return !isNaN(expiryDate.getTime()) && expiryDate > new Date();
};

const formatAnnouncementDate = (dateString) => {
  if (!dateString) return 'No expiry';
  const s = formatUsDateFromIso(dateString);
  return s || dateString;
};

const formatAnnouncementCreatedDate = (dateString) => {
  if (!dateString) return 'Unknown';
  const s = formatUsDateFromDateTime(dateString);
  return s || dateString;
};

const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + '...';
};

const AnnouncementListItem = ({ announcement, userProfile, onSelect }) => {
  const attachments = Array.isArray(announcement.attachments) ? announcement.attachments : [];
  const showScope = canManageAnnouncements(userProfile.role);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(announcement)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(announcement);
        }
      }}
      className="w-full text-left bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-[#674936] hover:shadow-sm transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <svg
            className="w-5 h-5 text-[#674936]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-semibold text-gray-800 text-sm truncate">{announcement.title}</span>
            {attachments.length > 0 && (
              <span
                className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0"
                title={`${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.414a4 4 0 10-5.656-5.656L4.343 11.172a6 6 0 108.485 8.485L20 12.5"
                  />
                </svg>
                {attachments.length}
              </span>
            )}
            {isAnnouncementActive(announcement) && (
              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-800 shrink-0">
                Active
              </span>
            )}
          </div>
          {announcement.description && (
            <p className="text-xs text-gray-500 line-clamp-2 mb-1">{announcement.description}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-400">
            {showScope ? (
              <>
                <span>Created: {formatAnnouncementCreatedDate(announcement.created_at)}</span>
                <span>Expires: {formatAnnouncementDate(announcement.expiry_date)}</span>
                <span>
                  Departments:{' '}
                  {Array.isArray(announcement.departments) && announcement.departments.length > 0
                    ? announcement.departments.join(', ')
                    : 'All departments'}
                </span>
                <span>
                  Positions:{' '}
                  {Array.isArray(announcement.positions) && announcement.positions.length > 0
                    ? announcement.positions.join(', ')
                    : 'All positions'}
                </span>
              </>
            ) : (
              <>
                <span>{formatAnnouncementCreatedDate(announcement.created_at)}</span>
                <span>By: {announcement.created_by || 'System'}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Announcements = ({
  userProfile,
  pendingNotificationAction,
  onNotificationActionHandled,
}) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const EMPTY_ANNOUNCEMENT = {
    title: '',
    description: '',
    departments: [],
    positions: [],
    expiry_date: '',
    newFiles: [],
  };

  const [newAnnouncement, setNewAnnouncement] = useState({ ...EMPTY_ANNOUNCEMENT });

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [popupMessage, setPopupMessage] = useState({ success: null, failure: null, info: null });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const departmentsFetchedRef = useRef(false);
  const [positionOptions, setPositionOptions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const positionsFetchedRef = useRef(false);
  const [departmentPickerSearch, setDepartmentPickerSearch] = useState('');
  const [positionPickerSearch, setPositionPickerSearch] = useState('');
  const [createExpiryDisplay, setCreateExpiryDisplay] = useState('');
  const [editExpiryDisplay, setEditExpiryDisplay] = useState('');
  /** After a successful create API call, clear the create form only when the user dismisses the success popup (OK). */
  const closeCreateFormAfterSuccessRef = useRef(false);
  /** After a successful update API call, clear the edit form only when the user dismisses the success popup (OK). */
  const closeEditFormAfterSuccessRef = useRef(false);

  const statusOptions = ['All', 'Active', 'Inactive'];

  const fetchAnnouncements = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const headers = await getAuthHeaders();
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });

      if (searchTerm) {
        params.append('q', searchTerm);
      }

      if (canManageAnnouncements(userProfile.role)) {
        if (filter === 'Active') {
          params.append('active', 'true');
        } else if (filter === 'Inactive') {
          params.append('active', 'false');
        }
      }
      
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/announcements?${params.toString()}`, {
        headers
      });
      
      if (response.data && typeof response.data === 'object') {
        setAnnouncements(response.data.announcements || []);
        setTotalCount(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
        setCurrentPage(response.data.page || 1);
        
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      
      if (error.response?.status === 401) {
        setError('Authentication failed. Please sign in again.');
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to view announcements.');
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to fetch announcements. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExpiryBlur = () => {
    const trimmed = createExpiryDisplay.trim();
    if (!trimmed) {
      setNewAnnouncement((prev) => ({ ...prev, expiry_date: '' }));
      return;
    }
    const iso = parseUsDateToIso(trimmed);
    if (!iso) {
      setNewAnnouncement((prev) => ({ ...prev, expiry_date: '' }));
      return;
    }
    if (iso < todayIsoLocal()) {
      setNewAnnouncement((prev) => ({ ...prev, expiry_date: '' }));
      setPopupMessage({ success: null, failure: 'Expiry date must be today or later.', info: null });
      return;
    }
    setNewAnnouncement((prev) => ({ ...prev, expiry_date: iso }));
    setCreateExpiryDisplay(formatUsDateFromIso(iso));
  };

  const handleEditExpiryBlur = () => {
    const trimmed = editExpiryDisplay.trim();
    if (!trimmed) {
      setEditingAnnouncement((prev) => (prev ? { ...prev, expiry_date: '' } : prev));
      return;
    }
    const iso = parseUsDateToIso(trimmed);
    if (!iso) {
      setEditingAnnouncement((prev) => (prev ? { ...prev, expiry_date: '' } : prev));
      return;
    }
    if (iso < todayIsoLocal()) {
      setEditingAnnouncement((prev) => (prev ? { ...prev, expiry_date: '' } : prev));
      setPopupMessage({ success: null, failure: 'Expiry date must be today or later.', info: null });
      return;
    }
    setEditingAnnouncement((prev) => (prev ? { ...prev, expiry_date: iso } : prev));
    setEditExpiryDisplay(formatUsDateFromIso(iso));
  };

  const handleCreateAnnouncement = async () => {
    const expiryIso = resolveIsoFromUsForm(createExpiryDisplay, newAnnouncement.expiry_date);
    if (!newAnnouncement.title || !newAnnouncement.description || !expiryIso) {
      setPopupMessage({
        success: null,
        failure: !expiryIso
          ? 'Please enter a valid expiry date as MM/DD/YYYY.'
          : 'Please fill in all required fields.',
        info: null
      });
      return;
    }
    if (expiryIso < todayIsoLocal()) {
      setPopupMessage({ success: null, failure: 'Expiry date must be today or later.', info: null });
      return;
    }

    try {
      setIsSubmitting(true);
      const headers = await getAuthHeaders();

      const formData = new FormData();
      formData.append('title', newAnnouncement.title);
      formData.append('description', newAnnouncement.description);
      formData.append('departments', (newAnnouncement.departments || []).join(','));
      formData.append('positions', (newAnnouncement.positions || []).join(','));
      formData.append('expiry_date', expiryIso);
      (newAnnouncement.newFiles || []).forEach((file) => {
        formData.append('files', file, file.name);
      });

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/announcements`, formData, {
        headers
      });

      closeCreateFormAfterSuccessRef.current = true;

      await fetchAnnouncements(currentPage);

      window.dispatchEvent(new CustomEvent('announcementsUpdated'));

      setPopupMessage({ success: 'Announcement created successfully!', failure: null, info: null });
    } catch (error) {
      console.error('Error creating announcement:', error);
      setPopupMessage({ success: null, failure: 'Failed to create announcement. Please try again.', info: null });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement({
      id: announcement.id,
      title: announcement.title,
      description: announcement.description,
      departments: Array.isArray(announcement.departments) ? announcement.departments : [],
      positions: Array.isArray(announcement.positions) ? announcement.positions : [],
      expiry_date: announcement.expiry_date ? announcement.expiry_date.split('T')[0] : '',
      attachments: Array.isArray(announcement.attachments) ? announcement.attachments : [],
      newFiles: [],
      removedAttachmentIds: []
    });
    setShowEditForm(true);
    setSelectedAnnouncement(null);
  };

  const handleUpdateAnnouncement = async () => {
    const expiryIso = resolveIsoFromUsForm(editExpiryDisplay, editingAnnouncement?.expiry_date);
    if (!editingAnnouncement.title || !editingAnnouncement.description || !expiryIso) {
      setPopupMessage({
        success: null,
        failure: !expiryIso
          ? 'Please enter a valid expiry date as MM/DD/YYYY.'
          : 'Please fill in all required fields.',
        info: null
      });
      return;
    }
    if (expiryIso < todayIsoLocal()) {
      setPopupMessage({ success: null, failure: 'Expiry date must be today or later.', info: null });
      return;
    }

    try {
      setIsUpdating(true);
      const headers = await getAuthHeaders();

      const formData = new FormData();
      formData.append('title', editingAnnouncement.title);
      formData.append('description', editingAnnouncement.description);
      formData.append('departments', (editingAnnouncement.departments || []).join(','));
      formData.append('positions', (editingAnnouncement.positions || []).join(','));
      formData.append('expiry_date', expiryIso);
      const removedIds = editingAnnouncement.removedAttachmentIds || [];
      if (removedIds.length > 0) {
        formData.append('removed_attachment_ids', removedIds.join(','));
      }
      (editingAnnouncement.newFiles || []).forEach((file) => {
        formData.append('new_files', file, file.name);
      });

      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/${editingAnnouncement.id}`, formData, {
        headers
      });

      closeEditFormAfterSuccessRef.current = true;

      await fetchAnnouncements(currentPage);
      
      window.dispatchEvent(new CustomEvent('announcementsUpdated'));
      
      setPopupMessage({ success: 'Announcement updated successfully!', failure: null, info: null });
    } catch (error) {
      console.error('Error updating announcement:', error);
      if (error.response?.status === 404) {
        setPopupMessage({ success: null, failure: 'Announcement not found. It may have been deleted.', info: null });
      } else if (error.response?.status === 403) {
        setPopupMessage({ success: null, failure: 'Access denied. You do not have permission to update this announcement.', info: null });
      } else {
        setPopupMessage({ success: null, failure: 'Failed to update announcement. Please try again.', info: null });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAnnouncement = (id) => {
    setAnnouncementToDelete(id);
    setShowDeleteConfirm(true);
    setSelectedAnnouncement(null);
  };

  const confirmDeleteAnnouncement = async () => {
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/${announcementToDelete}`, {
        headers
      });
      
      await fetchAnnouncements(currentPage);
      
      window.dispatchEvent(new CustomEvent('announcementsUpdated'));
      
      setPopupMessage({ success: 'Announcement deleted successfully!', failure: null, info: null });
      setShowDeleteConfirm(false);
      setAnnouncementToDelete(null);
    } catch (error) {
      console.error('Error deleting announcement:', error);
      if (error.response?.status === 404) {
        setPopupMessage({ success: null, failure: 'Announcement not found. It may have already been deleted.', info: null });
      } else if (error.response?.status === 403) {
        setPopupMessage({ success: null, failure: 'Access denied. You do not have permission to delete this announcement.', info: null });
      } else if (error.response?.status === 500) {
        setPopupMessage({ success: null, failure: 'Server error. Please try again later.', info: null });
      } else {
        setPopupMessage({ success: null, failure: 'Failed to delete announcement. Please try again.', info: null });
      }
      setShowDeleteConfirm(false);
      setAnnouncementToDelete(null);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchAnnouncements(currentPage);
    }, searchTerm ? 500 : 0);
    
    return () => clearTimeout(handler);
  }, [currentPage, filter, searchTerm]);

  useEffect(() => {
    if (!pendingNotificationAction || pendingNotificationAction.type !== 'announcement') {
      return undefined;
    }

    let cancelled = false;

    const openFromNotification = async () => {
      try {
        const headers = await getAuthHeaders();
        const announcement = await fetchAnnouncementById(
          pendingNotificationAction.entityId,
          headers
        );
        if (!cancelled) {
          setSelectedAnnouncement(announcement);
        }
      } catch (error) {
        if (!cancelled) {
          setPopupMessage({
            success: null,
            failure: null,
            info: parseNotificationActionError(error),
          });
        }
      } finally {
        if (!cancelled && onNotificationActionHandled) {
          onNotificationActionHandled();
        }
      }
    };

    openFromNotification();

    return () => {
      cancelled = true;
    };
  }, [pendingNotificationAction, onNotificationActionHandled]);

  useEffect(() => {
    if (!(showCreateForm || showEditForm)) return;

    const fetchDepartments = async () => {
      if (departmentsFetchedRef.current || departmentsLoading) return;
      try {
        setDepartmentsLoading(true);
        const headers = await getAuthHeaders();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/system/departments`,
          { headers }
        );
        const departments = Array.isArray(response.data?.departments)
          ? response.data.departments
          : [];
        setDepartmentOptions(departments);
        departmentsFetchedRef.current = true;
      } catch (err) {
        console.error('Error fetching departments:', err);
        setPopupMessage({
          success: null,
          failure: 'Failed to load departments. Please try again.',
          info: null
        });
      } finally {
        setDepartmentsLoading(false);
      }
    };

    const fetchPositions = async () => {
      if (positionsFetchedRef.current || positionsLoading) return;
      try {
        setPositionsLoading(true);
        const headers = await getAuthHeaders();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/system/positions`,
          { headers }
        );
        const positions = Array.isArray(response.data?.positions)
          ? response.data.positions
          : [];
        setPositionOptions(positions);
        positionsFetchedRef.current = true;
      } catch (err) {
        console.error('Error fetching positions:', err);
        setPopupMessage({
          success: null,
          failure: 'Failed to load positions. Please try again.',
          info: null
        });
      } finally {
        setPositionsLoading(false);
      }
    };

    fetchDepartments();
    fetchPositions();
  }, [showCreateForm, showEditForm]);

  useEffect(() => {
    if (showCreateForm || showEditForm) {
      setDepartmentPickerSearch('');
      setPositionPickerSearch('');
    }
  }, [showCreateForm, showEditForm]);

  useEffect(() => {
    if (!showCreateForm) {
      setNewAnnouncement({ ...EMPTY_ANNOUNCEMENT });
      setCreateExpiryDisplay('');
    }
  }, [showCreateForm]);

  useEffect(() => {
    if (!showCreateForm) return;
    setCreateExpiryDisplay(
      newAnnouncement.expiry_date ? formatUsDateFromIso(newAnnouncement.expiry_date) : ''
    );
  }, [showCreateForm]);

  useEffect(() => {
    if (!showEditForm || !editingAnnouncement) return;
    setEditExpiryDisplay(
      editingAnnouncement.expiry_date ? formatUsDateFromIso(editingAnnouncement.expiry_date) : ''
    );
  }, [showEditForm, editingAnnouncement?.id, editingAnnouncement?.expiry_date]);

  const filteredDepartmentsForPicker = useMemo(() => {
    const q = departmentPickerSearch.trim().toLowerCase();
    if (!q) return departmentOptions;
    return departmentOptions.filter((d) => String(d).toLowerCase().includes(q));
  }, [departmentOptions, departmentPickerSearch]);

  const filteredPositionsForPicker = useMemo(() => {
    const q = positionPickerSearch.trim().toLowerCase();
    if (!q) return positionOptions;
    return positionOptions.filter((p) => String(p).toLowerCase().includes(q));
  }, [positionOptions, positionPickerSearch]);

  const handleDepartmentChange = (dept) => {
    setNewAnnouncement(prev => ({
      ...prev,
      departments: prev.departments.includes(dept)
        ? prev.departments.filter(d => d !== dept)
        : [...prev.departments, dept]
    }));
  };

  const handleEditDepartmentChange = (dept) => {
    setEditingAnnouncement(prev => ({
      ...prev,
      departments: prev.departments.includes(dept)
        ? prev.departments.filter(d => d !== dept)
        : [...prev.departments, dept]
    }));
  };

  const handlePositionChange = (pos) => {
    setNewAnnouncement(prev => ({
      ...prev,
      positions: prev.positions.includes(pos)
        ? prev.positions.filter(p => p !== pos)
        : [...prev.positions, pos]
    }));
  };

  const handleEditPositionChange = (pos) => {
    setEditingAnnouncement(prev => ({
      ...prev,
      positions: (prev.positions || []).includes(pos)
        ? prev.positions.filter(p => p !== pos)
        : [...(prev.positions || []), pos]
    }));
  };

  const remainingEditCapacity = editingAnnouncement
    ? MAX_ATTACHMENTS
      - (editingAnnouncement.attachments || []).filter(
          (att) => !(editingAnnouncement.removedAttachmentIds || []).includes(att.id)
        ).length
      - (editingAnnouncement.newFiles || []).length
    : MAX_ATTACHMENTS;

  const handleAddCreateFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (picked.length === 0) return;

    const currentCount = (newAnnouncement.newFiles || []).length;
    if (currentCount + picked.length > MAX_ATTACHMENTS) {
      setPopupMessage({
        success: null,
        failure: `You can attach at most ${MAX_ATTACHMENTS} files per announcement.`,
        info: null
      });
      return;
    }

    const validationError = validateAttachmentFiles(picked);
    if (validationError) {
      setPopupMessage({ success: null, failure: validationError, info: null });
      return;
    }

    setNewAnnouncement(prev => ({
      ...prev,
      newFiles: [...(prev.newFiles || []), ...picked]
    }));
  };

  const handleRemoveCreateFile = (index) => {
    setNewAnnouncement(prev => ({
      ...prev,
      newFiles: (prev.newFiles || []).filter((_, idx) => idx !== index)
    }));
  };

  const handleAddEditFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (picked.length === 0) return;

    if (picked.length > remainingEditCapacity) {
      setPopupMessage({
        success: null,
        failure: `Only ${remainingEditCapacity} more attachment slot(s) available.`,
        info: null
      });
      return;
    }

    const validationError = validateAttachmentFiles(picked);
    if (validationError) {
      setPopupMessage({ success: null, failure: validationError, info: null });
      return;
    }

    setEditingAnnouncement(prev => ({
      ...prev,
      newFiles: [...(prev.newFiles || []), ...picked]
    }));
  };

  const handleRemoveEditNewFile = (index) => {
    setEditingAnnouncement(prev => ({
      ...prev,
      newFiles: (prev.newFiles || []).filter((_, idx) => idx !== index)
    }));
  };

  const handleRemoveExistingAttachment = (attachmentId) => {
    setEditingAnnouncement(prev => ({
      ...prev,
      removedAttachmentIds: [...(prev.removedAttachmentIds || []), attachmentId]
    }));
  };

  const handleRestoreExistingAttachment = (attachmentId) => {
    setEditingAnnouncement(prev => ({
      ...prev,
      removedAttachmentIds: (prev.removedAttachmentIds || []).filter((id) => id !== attachmentId)
    }));
  };

  const handleFilterChange = (status) => {
    if (canManageAnnouncements(userProfile.role)) {
      setFilter(status);
      setCurrentPage(1);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleAnnouncementPopupClose = () => {
    const shouldCloseCreateForm = closeCreateFormAfterSuccessRef.current;
    const shouldCloseEditForm = closeEditFormAfterSuccessRef.current;
    closeCreateFormAfterSuccessRef.current = false;
    closeEditFormAfterSuccessRef.current = false;
    setPopupMessage({ success: null, failure: null, info: null });
    if (shouldCloseCreateForm) {
      setShowCreateForm(false);
    }
    if (shouldCloseEditForm) {
      setEditingAnnouncement(null);
      setShowEditForm(false);
      setEditExpiryDisplay('');
    }
  };

  return (
    <div className="leave-management relative w-full min-w-0 max-w-full">
      <ReusablePopup
        success={popupMessage.success}
        failure={popupMessage.failure}
        info={popupMessage.info}
        onClose={handleAnnouncementPopupClose}
      />
      
      <AnnouncementDetailsPopup 
        announcement={selectedAnnouncement}
        userProfile={userProfile}
        onClose={() => setSelectedAnnouncement(null)}
        onEdit={handleEditAnnouncement}
        onDelete={handleDeleteAnnouncement}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full max-h-[min(90vh,calc(100dvh-1.5rem))] overflow-y-auto overscroll-contain my-auto">
            <div className="flex items-start gap-3 sm:gap-4">
              <span className="text-2xl shrink-0 text-red-600" aria-hidden="true">⚠️</span>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gray-800 mb-4">
                  Confirm Delete
                </h2>
                <p className="text-gray-600 mb-6 text-sm sm:text-base break-words">
                  Are you sure you want to delete this announcement? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 w-full">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setAnnouncementToDelete(null);
                }}
                className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2.5 rounded-lg hover:bg-gray-600 transition-colors duration-300 font-semibold touch-manipulation"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteAnnouncement}
                className="w-full sm:w-auto bg-red-500 text-white px-4 py-2.5 rounded-lg hover:bg-red-600 transition-colors duration-300 font-semibold touch-manipulation"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="section-header bg-[#674936] text-white p-3 sm:p-4 rounded flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center !mb-4 sm:!mb-8">
        <h3 className="flex gap-2 sm:gap-4 text-white text-[1rem] md:text-[1.6rem] font-bold break-words min-w-0">
          📢 Announcements
        </h3>
        {canManageAnnouncements(userProfile.role) && (
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            className="w-full sm:w-auto shrink-0 bg-white text-[#674936] border-none px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg cursor-pointer text-sm transition-colors duration-300 font-semibold font-['Titillium_Web'] hover:bg-[#e2dbd6] touch-manipulation text-center"
          >
            Create New Announcement
          </button>
        )}
      </div>

      <div className="my-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between w-full min-w-0">
        {canManageAnnouncements(userProfile.role) && (
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto min-w-0 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
            {statusOptions.map((status) => (
              <button
                type="button"
                key={status}
                onClick={() => handleFilterChange(status)}
                className={`shrink-0 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition touch-manipulation ${filter === status
                  ? 'bg-white text-[#674936] shadow'
                  : 'bg-[#674936] text-white hover:bg-[#ebebeb] hover:shadow hover:text-[#674936]'
                  }`}
              >
                {status}
              </button>
            ))}
          </div>
        )}
        
        <div
          className={`flex items-center gap-2 w-full min-w-0 sm:max-w-md lg:max-w-[400px] ${canManageAnnouncements(userProfile.role) ? '' : 'sm:ml-auto'}`}
        >
          <div className="form-field w-full relative">
            <input
              type="text"
              placeholder="Search announcements..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full !pr-[20px] border rounded focus:outline-none focus:border-gray-500"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-[20px] top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Create New Announcement Form */}
      {showCreateForm && (
        <div className="modal !items-start sm:!items-center !py-4 sm:!py-6 overflow-y-auto">
          <div className="modal-content !p-4 sm:!p-6 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain w-full max-w-[min(1000px,calc(100vw-2rem))] my-auto shadow-xl">
            <h3 className="w-full flex justify-center items-center text-center px-1">Create New Announcement</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent"
                  placeholder="Enter announcement title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={newAnnouncement.description}
                  onChange={(e) => setNewAnnouncement({...newAnnouncement, description: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent"
                  rows="4"
                  placeholder="Enter announcement description..."
                />
              </div>

              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3 mb-2">
                  <label className="block text-sm font-medium text-gray-700 min-w-0 flex-1">
                    Departments
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (leave empty to target all departments)
                    </span>
                  </label>
                  <input
                    type="search"
                    placeholder="Search departments..."
                    value={departmentPickerSearch}
                    onChange={(e) => setDepartmentPickerSearch(e.target.value)}
                    disabled={departmentsLoading || departmentOptions.length === 0}
                    className="w-full sm:w-56 lg:w-64 shrink-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    aria-label="Filter departments list"
                  />
                </div>
                {departmentsLoading ? (
                  <p className="text-sm text-gray-500">Loading departments...</p>
                ) : departmentOptions.length === 0 ? (
                  <p className="text-sm text-gray-500">No departments found.</p>
                ) : filteredDepartmentsForPicker.length === 0 ? (
                  <p className="text-sm text-gray-500">No departments match your search.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {filteredDepartmentsForPicker.map((dept) => (
                      <label key={dept} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={newAnnouncement.departments.includes(dept)}
                          onChange={() => handleDepartmentChange(dept)}
                          className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936]"
                        />
                        <span className="text-sm text-gray-700">{dept}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3 mb-2">
                  <label className="block text-sm font-medium text-gray-700 min-w-0 flex-1">
                    Positions
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (leave empty to target all positions)
                    </span>
                  </label>
                  <input
                    type="search"
                    placeholder="Search positions..."
                    value={positionPickerSearch}
                    onChange={(e) => setPositionPickerSearch(e.target.value)}
                    disabled={positionsLoading || positionOptions.length === 0}
                    className="w-full sm:w-56 lg:w-64 shrink-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    aria-label="Filter positions list"
                  />
                </div>
                {positionsLoading ? (
                  <p className="text-sm text-gray-500">Loading positions...</p>
                ) : positionOptions.length === 0 ? (
                  <p className="text-sm text-gray-500">No positions found.</p>
                ) : filteredPositionsForPicker.length === 0 ? (
                  <p className="text-sm text-gray-500">No positions match your search.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {filteredPositionsForPicker.map((pos) => (
                      <label key={pos} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={newAnnouncement.positions.includes(pos)}
                          onChange={() => handlePositionChange(pos)}
                          className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936]"
                        />
                        <span className="text-sm text-gray-700">{pos}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="announcement-expiry-create">
                  Expiry Date *
                </label>
                <div className="relative">
                  <input
                    id="announcement-expiry-create"
                    type="text"
                    inputMode="numeric"
                    placeholder="mm/dd/yyyy"
                    autoComplete="off"
                    value={createExpiryDisplay}
                    onChange={(e) => setCreateExpiryDisplay(normalizeUsDateTyping(e.target.value))}
                    onBlur={handleCreateExpiryBlur}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent"
                    maxLength={10}
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9">
                    <span
                      className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md text-gray-500"
                      aria-hidden="true"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </span>
                    <input
                      type="date"
                      value={newAnnouncement.expiry_date || ''}
                      min={todayIsoLocal()}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) {
                          setNewAnnouncement((prev) => ({ ...prev, expiry_date: '' }));
                          setCreateExpiryDisplay('');
                          return;
                        }
                        setNewAnnouncement((prev) => ({ ...prev, expiry_date: v }));
                        setCreateExpiryDisplay(formatUsDateFromIso(v));
                      }}
                      aria-label="Open calendar to pick expiry date"
                      className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Enter date as MM/DD/YYYY or use the calendar. Must be today or later.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attachments
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    (PDF, Word, Excel, PowerPoint, images. Max {MAX_ATTACHMENTS} files, 10 MB each)
                  </span>
                </label>
                <input
                  type="file"
                  multiple
                  accept={ATTACHMENT_ACCEPT}
                  onChange={handleAddCreateFiles}
                  disabled={(newAnnouncement.newFiles || []).length >= MAX_ATTACHMENTS}
                  className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#674936] file:text-white hover:file:bg-[#503220] disabled:opacity-60"
                />
                {(newAnnouncement.newFiles || []).length > 0 && (
                  <ul className="mt-3 space-y-2 max-h-48 overflow-y-auto overscroll-contain pr-1 rounded-lg border border-transparent">
                    {newAnnouncement.newFiles.map((file, idx) => (
                      <li
                        key={`${file.name}-${idx}`}
                        className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 mr-3">
                          <p className="text-gray-800 font-medium truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveCreateFile(idx)}
                          className="text-red-600 hover:text-red-800 text-sm font-semibold"
                          title="Remove file"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-4 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-300 touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateAnnouncement}
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-6 py-3 bg-[#674936] text-white rounded-lg hover:bg-[#503220] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                >
                  {isSubmitting ? 'Creating...' : 'Create Announcement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Announcement Form */}
      {showEditForm && editingAnnouncement && (
        <div className="modal !items-start sm:!items-center !py-4 sm:!py-6 overflow-y-auto">
          <div className="modal-content !p-4 sm:!p-6 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain w-full max-w-[min(1000px,calc(100vw-2rem))] my-auto shadow-xl">
            <h3 className="w-full flex justify-center items-center text-center px-1">Edit Announcement</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={editingAnnouncement.title}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, title: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent"
                  placeholder="Enter announcement title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={editingAnnouncement.description}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, description: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent"
                  rows="4"
                  placeholder="Enter announcement description..."
                />
              </div>

              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3 mb-2">
                  <label className="block text-sm font-medium text-gray-700 min-w-0 flex-1">
                    Departments
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (leave empty to target all departments)
                    </span>
                  </label>
                  <input
                    type="search"
                    placeholder="Search departments..."
                    value={departmentPickerSearch}
                    onChange={(e) => setDepartmentPickerSearch(e.target.value)}
                    disabled={departmentsLoading || departmentOptions.length === 0}
                    className="w-full sm:w-56 lg:w-64 shrink-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    aria-label="Filter departments list"
                  />
                </div>
                {departmentsLoading ? (
                  <p className="text-sm text-gray-500">Loading departments...</p>
                ) : departmentOptions.length === 0 ? (
                  <p className="text-sm text-gray-500">No departments found.</p>
                ) : filteredDepartmentsForPicker.length === 0 ? (
                  <p className="text-sm text-gray-500">No departments match your search.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {filteredDepartmentsForPicker.map((dept) => (
                      <label key={dept} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={(editingAnnouncement.departments || []).includes(dept)}
                          onChange={() => handleEditDepartmentChange(dept)}
                          className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936]"
                        />
                        <span className="text-sm text-gray-700">{dept}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3 mb-2">
                  <label className="block text-sm font-medium text-gray-700 min-w-0 flex-1">
                    Positions
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (leave empty to target all positions)
                    </span>
                  </label>
                  <input
                    type="search"
                    placeholder="Search positions..."
                    value={positionPickerSearch}
                    onChange={(e) => setPositionPickerSearch(e.target.value)}
                    disabled={positionsLoading || positionOptions.length === 0}
                    className="w-full sm:w-56 lg:w-64 shrink-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    aria-label="Filter positions list"
                  />
                </div>
                {positionsLoading ? (
                  <p className="text-sm text-gray-500">Loading positions...</p>
                ) : positionOptions.length === 0 ? (
                  <p className="text-sm text-gray-500">No positions found.</p>
                ) : filteredPositionsForPicker.length === 0 ? (
                  <p className="text-sm text-gray-500">No positions match your search.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {filteredPositionsForPicker.map((pos) => (
                      <label key={pos} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={(editingAnnouncement.positions || []).includes(pos)}
                          onChange={() => handleEditPositionChange(pos)}
                          className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936]"
                        />
                        <span className="text-sm text-gray-700">{pos}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="announcement-expiry-edit">
                  Expiry Date *
                </label>
                <div className="relative">
                  <input
                    id="announcement-expiry-edit"
                    type="text"
                    inputMode="numeric"
                    placeholder="mm/dd/yyyy"
                    autoComplete="off"
                    value={editExpiryDisplay}
                    onChange={(e) => setEditExpiryDisplay(normalizeUsDateTyping(e.target.value))}
                    onBlur={handleEditExpiryBlur}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent"
                    maxLength={10}
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9">
                    <span
                      className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md text-gray-500"
                      aria-hidden="true"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </span>
                    <input
                      type="date"
                      value={editingAnnouncement?.expiry_date || ''}
                      min={todayIsoLocal()}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) {
                          setEditingAnnouncement((prev) => (prev ? { ...prev, expiry_date: '' } : prev));
                          setEditExpiryDisplay('');
                          return;
                        }
                        setEditingAnnouncement((prev) => (prev ? { ...prev, expiry_date: v } : prev));
                        setEditExpiryDisplay(formatUsDateFromIso(v));
                      }}
                      aria-label="Open calendar to pick expiry date"
                      className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Enter date as MM/DD/YYYY or use the calendar. Must be today or later.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attachments
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    (Max {MAX_ATTACHMENTS} files total, 10 MB each.
                    {' '}{remainingEditCapacity} slot{remainingEditCapacity === 1 ? '' : 's'} remaining)
                  </span>
                </label>

                {(editingAnnouncement.attachments || []).length > 0 && (
                  <ul className="mb-3 space-y-2 max-h-48 overflow-y-auto overscroll-contain pr-1 rounded-lg">
                    {(editingAnnouncement.attachments || []).map((att) => {
                      const isRemoved = (editingAnnouncement.removedAttachmentIds || []).includes(att.id);
                      return (
                        <li
                          key={att.id}
                          className={`flex items-center justify-between border rounded-lg px-3 py-2 text-sm ${isRemoved ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}
                        >
                          <div className="min-w-0 mr-3">
                            <p className={`font-medium truncate ${isRemoved ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                              {att.file_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(att.size)}
                              {isRemoved ? ' • marked for removal' : ''}
                            </p>
                          </div>
                          {isRemoved ? (
                            <button
                              type="button"
                              onClick={() => handleRestoreExistingAttachment(att.id)}
                              className="text-[#674936] hover:underline text-sm font-semibold"
                            >
                              Undo
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRemoveExistingAttachment(att.id)}
                              className="text-red-600 hover:text-red-800 text-sm font-semibold"
                              title="Remove file"
                            >
                              Remove
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                <input
                  type="file"
                  multiple
                  accept={ATTACHMENT_ACCEPT}
                  onChange={handleAddEditFiles}
                  disabled={remainingEditCapacity <= 0}
                  className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#674936] file:text-white hover:file:bg-[#503220] disabled:opacity-60"
                />

                {(editingAnnouncement.newFiles || []).length > 0 && (
                  <ul className="mt-3 space-y-2 max-h-40 overflow-y-auto overscroll-contain pr-1 rounded-lg border border-transparent">
                    {editingAnnouncement.newFiles.map((file, idx) => (
                      <li
                        key={`${file.name}-${idx}`}
                        className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 mr-3">
                          <p className="text-gray-800 font-medium truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)} • new</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveEditNewFile(idx)}
                          className="text-red-600 hover:text-red-800 text-sm font-semibold"
                          title="Remove file"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-4 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingAnnouncement(null);
                  }}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-300 touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdateAnnouncement}
                  disabled={isUpdating}
                  className="w-full sm:w-auto px-6 py-3 bg-[#674936] text-white rounded-lg hover:bg-[#503220] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                >
                  {isUpdating ? 'Updating...' : 'Update Announcement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button
            onClick={() => fetchAnnouncements(currentPage)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && announcements.length === 0 && (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="text-6xl mb-4">📢</div>
            <p className="text-gray-500 text-lg">
              {searchTerm || (canManageAnnouncements(userProfile.role) && filter !== 'All')
                ? "No announcements match your current filter or search."
                : canManageAnnouncements(userProfile.role)
                  ? "No announcements yet. Create your first one above!"
                  : "No announcements yet."
              }
            </p>
          </div>
        </div>
      )}

      {/* Announcements List */}
      {!loading && !error && announcements.length > 0 && (
        <div className="relative overflow-x-auto border border-gray-200 rounded-lg w-full shadow-sm mx-auto mt-4">
          <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
            <thead className="bg-[#674936]">
              <tr>
                <th className="px-4 py-3 text-white font-semibold">Announcement</th>
                <th className="px-4 py-3 text-white font-semibold">Attachments</th>
                <th className="px-4 py-3 text-white font-semibold">Status</th>
                <th className="px-4 py-3 text-white font-semibold">Expires</th>
                <th className="px-4 py-3 text-white font-semibold">Departments</th>
                <th className="px-4 py-3 text-white font-semibold">Positions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {announcements.map((announcement) => {
                const active = isAnnouncementActive(announcement);
                const attachmentsCount =
                  Array.isArray(announcement.attachments) && announcement.attachments.length > 0
                    ? announcement.attachments.length
                    : 0;

                return (
                  <tr
                    key={announcement.id}
                    role="button"
                    tabIndex={0}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedAnnouncement(announcement)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedAnnouncement(announcement);
                      }
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-800 truncate max-w-[28rem]">
                          {announcement.title || '-'}
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-2 mt-1">
                          {truncateText(announcement.description || '-', 120)}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {attachmentsCount > 0 ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded bg-gray-100 text-gray-700"
                          title={`${attachmentsCount} attachment${attachmentsCount === 1 ? '' : 's'}`}
                        >
                          {attachmentsCount}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {active ? (
                        <span className="text-xs font-medium px-2 py-1 rounded bg-green-100 text-green-800 inline-flex">
                          Active
                        </span>
                      ) : (
                        <span className="text-xs font-medium px-2 py-1 rounded bg-gray-100 text-gray-600 inline-flex">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{formatAnnouncementDate(announcement.expiry_date)}</td>
                    <td className="px-4 py-3 break-words">
                      {Array.isArray(announcement.departments) && announcement.departments.length > 0
                        ? announcement.departments.join(', ')
                        : 'All departments'}
                    </td>
                    <td className="px-4 py-3 break-words">
                      {Array.isArray(announcement.positions) && announcement.positions.length > 0
                        ? announcement.positions.join(', ')
                        : 'All positions'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {!loading && announcements.length > 0 && (
        <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 mt-6 sm:mt-8 px-2 pb-6 w-full min-w-0">
          <button
            type="button"
            onClick={() => fetchAnnouncements(currentPage - 1)}
            disabled={currentPage === 1}
            className={`min-h-[44px] px-4 py-2 rounded text-sm sm:text-base touch-manipulation shrink-0 ${currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#674936] text-white hover:bg-[#503220]'}`}
          >
            Previous
          </button>
          <span className="text-sm sm:text-base text-center text-gray-700 shrink-0">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => fetchAnnouncements(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`min-h-[44px] px-4 py-2 rounded text-sm sm:text-base touch-manipulation shrink-0 ${currentPage === totalPages ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#674936] text-white hover:bg-[#503220]'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Announcements;
